import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, theme, Button, message, Input, Modal } from "antd";
import { TagOutlined, PlusOutlined } from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { ClaimTable } from "../../components/Table";
import { AddClaimModal } from "../../components/ModalForm";
import { EditClaimModal } from "../../components/ModalForm";
import ContinuationModal from "../../components/ModalForm/Claim/ContinuationModal";

import { AuthContext } from "../../context/AuthContext";
import { ClaimContext } from "../../context/ClaimContext";
import { TourContext } from "../../context/TourContext";

import {
  getMemberPolicyApi,
  addContinuationMemberApi,
} from "../../services/Member/MemberApis";
import {
  getGroupsApi,
  addGroupApi,
  editGroupApi,
  removeGroupApi,
} from "../../services/Group/GroupApis";
import {
  getCompanyClaimsApi,
  getClaimApi,
  addClaimApi,
  addExternalClaimApi,
  editClaimApi,
  searchGroupClaimsApi,
} from "../../services/Claim/ClaimApis";

const { Content } = Layout;
const { Search } = Input;

const ClaimsPage: React.FC = () => {
  const [claims, setClaims] = useState<any>([]);
  const [claim, setClaim] = useState<any>({});
  const [filtered, setFiltered] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>(
    () => sessionStorage.getItem("claimsSearch") || ""
  );
  const [show, setShow] = useState(false);
  const [state, setState] = useState({
    add: false,
    update: false,
    continuation: false,
  });
  const [isActive, setActive] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false); // State for confirmation modal
  const [groupToDelete, setGroupToDelete] = useState<string>(""); // State for confirmation modal
  const [key, setKey] = useState<string>(""); // State for confirmation modal
  const [member, setMember] = useState<string>("");
  const { currentUser, currentGroup } = useContext(AuthContext);
  const { setClaimData, claimData, resetClaimData } = useContext(ClaimContext);
  const { refAddClaim, markPageAsVisited, pages, setOpen, setPageLoaded } =
    useContext(TourContext);

  const navigate = useNavigate();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showModal = () => {
    setShow(!show);
    setState({
      add: true,
      update: false,
      continuation: false,
    });
  };

  const showEditModal = async (data: any) => {
    setActive(true);
    try {
      // Fetch the full claim so we always get documents and full deceased/policy info,
      // regardless of whether the row came from the list or search endpoints.
      const res = await getClaimApi(data.id);
      const full = res?.data ?? data;

      // To populate the correct Row in CoveredLive table and Render Documents correctly
      setClaimData({
        id: full.DeceasedId,
        Documents: full.ClaimDocument ?? [],
      });

      setClaim({
        id: full.id,
        MemberID: full.Policy?.MemberID,
        PolicyNumber: full.Policy?.PolicyNumber,
        DateOfDeath: full.DateOfDeath && dayjs(full.DateOfDeath).isValid()
          ? dayjs(full.DateOfDeath)
          : undefined,
        CauseOfDeath: full.CauseOfDeath,
        Notes: full.Notes || full.AdditionalNotes || "",
        IsExternal: full.IsExternal,
        // External-only fields, harmless when undefined for internal claims
        DeceasedName: full.DeceasedName,
        DeceasedSurname: full.DeceasedSurname,
        DeceasedIdNumber: full.DeceasedIdNumber,
        DeceasedType: full.DeceasedType,
        BenName: full.BenName,
        BenSurname: full.BenSurname,
        BenIdNumber: full.BenIdNumber,
        ClaimAmount: full.ClaimAmount,
        Bank: full.Bank,
        AccountNumber: full.AccountNumber,
        AccountType: full.AccountType,
        BranchCode: full.BranchCode,
      });
      setShow((prev) => !prev);
      setState({
        add: false,
        update: true,
        continuation: false,
      });
    } catch (err) {
      console.log(err);
      message.error("Failed to load claim for editing.");
    } finally {
      setActive(false);
    }
  };

  const showContinuationModal = async (data: any) => {
    setActive(true);

    return await getMemberPolicyApi(data.Policy.PolicyNumber)
      .then((res) => {
        // Check if the deceased ID number matches the member's ID number
        const isValidContinuation = data.DeceasedIdNumber === res.data.IdNumber;

        if(!isValidContinuation) {
          message.error("Deceased ID Number does not match Member's ID Number.");
          setActive(false);
          return;
        }

        setMember(res.data);
        setClaim(data);
        setShow(!show);
        setState({
          add: false,
          update: false,
          continuation: true,
        });
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleManage = (id: string) => {
    navigate(`/claims/${id}`);
  };

  const handleAdd = async (data: any) => {
    setActive(true);

    const { ClaimType, ...payload } = data;
    const request =
      ClaimType === "External"
        ? addExternalClaimApi(payload, currentUser.id, currentGroup)
        : addClaimApi(payload, currentUser.id, currentGroup);

    return await request
      .then(() => {
        return getCompanyClaimsApi(String(currentGroup));
      })
      .then((res) => {
        setClaims(res.data);
        setActive(false);
        resetClaimData();
        return { success: true };
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
        return { success: false, message: err?.response?.data?.message ?? err?.message ?? "Failed to submit claim." };
      });
  };

  const handleUpdate = async (id: string, data: any) => {
    setActive(true);

    return await editClaimApi(id, data)
      .then((res) => {
        const uppdatedClaims = claims.map((claim: any) => {
          if (id === claim.id) {
            return { ...claim, ...res.data };
          }
          return claim;
        });

        setClaims(uppdatedClaims);
        setActive(false);
        return { success: true };
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
        return { success: false, message: err?.response?.data?.message ?? err?.message ?? "Failed to update claim." };
      });
  };

  const handleDelete = (id: string, key: string) => {
    setKey(key);
    setConfirmDelete(true);
    setGroupToDelete(id);
  };

  const handleContinuation = async (id: string, data: any, userId: any) => {
    setActive(true);

    return await addContinuationMemberApi(id, data, userId, claim.id)
      .then((res) => {
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const onSearch = async (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) sessionStorage.setItem("claimsSearch", value);
    else sessionStorage.removeItem("claimsSearch");
    if (value !== "") {
      const result = await searchGroupClaimsApi(String(currentGroup), value);
      const claimIds = new Set(claims.map((c: any) => c.id));
      setFiltered(result.data.filter((c: any) => claimIds.has(c.id)));
    } else {
      setFiltered([]);
    }
  };

  const onCancelDelete = () => {
    setConfirmDelete(false);
    setGroupToDelete("");
  };

  const onConfirmDelete = () => {
    setConfirmDelete(false);
    setActive(true);

    removeGroupApi(groupToDelete)
      .then((res) => {
        getGroupsApi()
          .then((res) => {
            setActive(false);
            setClaims(res.data);
          })
          .catch((err) => {
            setActive(false);
            console.log(err);
          });
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  useEffect(() => {
    getCompanyClaimsApi(String(currentGroup))
      .then((res) => {
        setClaims(res.data);
        setActive(false);
        resetClaimData();
        setPageLoaded(true);
        setOpen(true);
      })

      .catch((err) => {
        console.log(err);
      });
  }, [currentGroup]);

  useEffect(() => {
    if (pages.AddMember) {
      markPageAsVisited("AddMember");
    }
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <TagOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Claims
              </h2>
              <div className="interactions" ref={refAddClaim}>
                <Button onClick={showModal} size="large">
                  <PlusOutlined style={{ paddingRight: "5px" }} />
                  Add Claim
                </Button>

                <div>
                  <Search
                    placeholder="Search"
                    size="large"
                    value={searchValue}
                    onChange={(e) => onSearch(e)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>

                {state.add && (
                  <AddClaimModal
                    show={show}
                    showModal={showModal}
                    handleAdd={handleAdd}
                  />
                )}

                {state.update && (
                  <EditClaimModal
                    show={show}
                    claim={claim}
                    showModal={showModal}
                    handleUpdate={handleUpdate}
                  />
                )}

                {state.continuation && (
                  <ContinuationModal
                    show={show}
                    member={member}
                    claimId={claim.id}
                    showModal={showModal}
                    handleAdd={handleContinuation}
                  />
                )}
              </div>

              <ClaimTable
                claims={claims}
                filtered={filtered}
                showEditModal={showEditModal}
                showContinuationModal={showContinuationModal}
                handleManage={handleManage}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* Confirmation Modal */}
      {key === "1" ? (
        <Modal
          title="Confirm Remove"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Group inactive?
        </Modal>
      ) : (
        <Modal
          title="Confirm Revert"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Group active?
        </Modal>
      )}
    </LoadingOverlay>
  );
};

export default ClaimsPage;
